<template>
    <div v-if="reading" class="reading-card" :class="statusBorderClass">
        <span v-if="showDeleteReadingButton" class="engineering-overlay">
            <v-btn icon="mdi-delete" size="x-small" color="red" variant="text" @click="deleteReading()" />
            <v-btn icon="mdi-alarm" size="x-small" color="red" variant="text" @click="expireReading()" />
        </span>

        <div class="reading-label">{{ title }}</div>

        <div class="reading-value-row">
            <span class="reading-value" :class="statusTextColor(reading.status)">{{ reading.value }}</span>
            <Tag v-if="reading.status === ReadingStatuses.Ok" severity="info" value="OK" class="reading-tag" />
            <Tag v-else-if="reading.status === ReadingStatuses.Low" severity="warn" value="Low" class="reading-tag" />
            <Tag v-else-if="reading.status === ReadingStatuses.High" severity="warn" value="High" class="reading-tag" />
            <Tag v-else-if="reading.status === ReadingStatuses.ReallyLow" severity="danger" value="Really Low" class="reading-tag" />
            <Tag v-else-if="reading.status === ReadingStatuses.ReallyHigh" severity="danger" value="Really High" class="reading-tag" />
            <Tag v-else-if="reading.status === ReadingStatuses.Invalid" severity="secondary" value="Invalid" class="reading-tag" />
        </div>

        <div class="reading-meta">
            <span v-if="showRaw" class="reading-source">AVG</span>
            <span v-else class="reading-source">{{ rawTitle }}</span>

            <v-tooltip v-if="date" :text="formatDateForVesselTimezone({ date, vesselTimezoneId })">
                <template v-slot:activator="{ props }">
                    <span v-bind="props" class="reading-date">{{ formatDistanceToNowStrict(date, { addSuffix: true }) }}</span>
                </template>
            </v-tooltip>
        </div>

        <div v-if="statusDuration" class="reading-status-duration" :class="statusTextColor(reading.status)">
            {{ statusDuration }}
        </div>

        <div v-if="reading.status === ReadingStatuses.Invalid && reading.statusReason" class="reading-status-reason">
            {{ reading.statusReason }}
        </div>

        <v-tooltip v-if="reading.statusHold?.description" :text="`expires in ${reading.statusHold.timeRemaining}`">
            <template v-slot:activator="{ props }">
                <div class="reading-hold" v-bind="props" :class="statusTextColor(reading.status)">
                    {{ reading.statusHold.description }}
                </div>
            </template>
        </v-tooltip>
    </div>
</template>

<script lang="ts" setup>
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, type PropType } from 'vue'
import { ReadingStatuses, ReadingTypesV1, type ReadingV1 } from '@geckoal/gecko-api-client'
import { useApi } from '@/api'
import { useGeneralStore } from '@/stores/general'
import { formatDateForVesselTimezone } from '@/lib/utils'
import Tag from 'primevue/tag'

const props = defineProps({
    readingType: {
        type: String,
        required: true
    },
    title: {
        type: String,
        required: true
    },
    reading: {
        type: Object as PropType<ReadingV1>,
        required: false
    },
    showRaw: {
        type: Boolean,
        default: false
    },
    rawTitle: {
        type: String,
        default: 'REPORTED'
    },
    accountId: {
        type: Number
    },
    vesselId: {
        type: Number
    },
    vesselTimezoneId: {
        type: String
    }
})

const date = computed(() => {
    if (props.reading) return props.reading.date
    return undefined
})

const emit = defineEmits(['updatedReading'])

const generalStore = useGeneralStore()

const showDeleteReadingButton = computed(() => {
    return generalStore.$state.showEngineeringOptions
})

const statusDuration = computed(() => {
    if (!props.reading) return undefined
    const s = props.reading.status
    if (
        s === ReadingStatuses.Invalid ||
        s === ReadingStatuses.Unknown ||
        s === ReadingStatuses.Ok
    ) return undefined
    if (!props.reading.statusSinceDate) return undefined
    return `for ${formatDistanceToNowStrict(props.reading.statusSinceDate)}`
})

const statusBorderClass = computed(() => {
    if (!props.reading) return 'border-grey'
    const s = props.reading.status
    if (s === ReadingStatuses.Ok) return 'border-blue'
    if (s === ReadingStatuses.High || s === ReadingStatuses.Low) return 'border-orange'
    if (s === ReadingStatuses.ReallyHigh || s === ReadingStatuses.ReallyLow) return 'border-red'
    return 'border-grey'
})

const statusTextColor = (status: ReadingStatuses) => {
    if (status === ReadingStatuses.Ok) return 'text-blue'
    if (status === ReadingStatuses.High || status === ReadingStatuses.Low) return 'text-orange'
    if (status === ReadingStatuses.ReallyHigh || status === ReadingStatuses.ReallyLow) return 'text-red'
    return 'text-grey'
}

const deleteReading = async () => {
    if (!props.accountId || !props.vesselId || !props.reading?.source) return
    try {
        await useApi().deleteVesselStatusReadingV2({
            expire: false,
            accountId: props.accountId,
            vesselId: props.vesselId,
            readingSource: props.reading?.source,
            readingType: props.readingType as ReadingTypesV1
        })
        emit('updatedReading')
    } catch (error) {
        console.error('Error deleting reading', error)
    }
}

const expireReading = async () => {
    if (!props.accountId || !props.vesselId || !props.reading?.source) return
    try {
        await useApi().deleteVesselStatusReadingV2({
            expire: true,
            accountId: props.accountId,
            vesselId: props.vesselId,
            readingSource: props.reading?.source,
            readingType: props.readingType as ReadingTypesV1
        })
        emit('updatedReading')
    } catch (error) {
        console.error('Error expiring reading', error)
    }
}
</script>

<style lang="scss" scoped>
.reading-card {
    position: relative;
    background: #fff;
    border-radius: 10px;
    padding: 10px 14px;
    min-width: 130px;
    max-width: 160px;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #e8e8e8;
    transition: box-shadow 0.15s, transform 0.15s;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &.border-blue { border-left-color: #2196F3; }
    &.border-orange { border-left-color: #FF9800; }
    &.border-red { border-left-color: #f44336; }
    &.border-grey { border-left-color: #bbb; }
}

.engineering-overlay {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 5;
    display: flex;
    gap: 2px;
}

.reading-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #888;
    margin-bottom: 4px;
}

.reading-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.reading-value {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.1;
}

.reading-tag {
    font-size: 0.65rem;
}

.reading-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.68rem;
    color: #999;
}

.reading-source {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.reading-date {
    cursor: help;
}

.reading-status-duration {
    font-size: 0.7rem;
    margin-top: 3px;
    font-weight: 500;
}

.reading-status-reason {
    font-size: 0.68rem;
    color: #999;
    margin-top: 2px;
}

.reading-hold {
    font-size: 0.68rem;
    margin-top: 2px;
    cursor: help;

    &:hover {
        text-decoration: underline;
    }
}

.text-blue { color: #1976D2; }
.text-orange { color: #E65100; }
.text-red { color: #c62828; }
.text-grey { color: #999; }
</style>
